import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import PageHeader from '../components/PageHeader';
import OrderMonthGroups from '../components/OrderMonthGroups';
import OrdersToolbar from '../components/OrdersToolbar';
import ReportExportBar from '../components/ReportExportBar';
import ConfirmDialog from '../components/ConfirmDialog';
import { filterOrders, sortOrders, groupOrdersByMonth } from '../utils/orderUtils';
import { advanceOrderStatus } from '../utils/orderActions';
import { runOrdersReportExport } from '../utils/reportExports';
import { STATUS_WORKFLOW } from '../utils/constants';

export default function OrdersListPage({
  title,
  subtitle,
  statusFilter,
  showAdvance = false,
  showCancel = false,
  poll = true,
}) {
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState('');
  const [cancelId, setCancelId] = useState(null);
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  const load = () =>
    api
      .getOrders(statusFilter)
      .then(setOrders)
      .catch((e) => setError(e.message));

  useEffect(() => {
    load();
    if (!poll) return undefined;
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, [statusFilter, poll]);

  const filtered = useMemo(() => {
    const f = filterOrders(orders, { search, dateFilter, customFrom, customTo });
    return sortOrders(f, sortBy);
  }, [orders, search, dateFilter, customFrom, customTo, sortBy]);

  const groups = useMemo(() => groupOrdersByMonth(filtered), [filtered]);

  const handleAdvance = (orderId, status) => {
    if (!status) return;
    setError('');
    advanceOrderStatus(api, orderId, status)
      .then(() => load())
      .catch((e) => setError(e.message));
  };

  const handleCancel = () => {
    const id = cancelId;
    if (!id) return;
    setError('');
    api
      .cancelOrder(id)
      .then(() => load())
      .catch((e) => setError(e.message))
      .finally(() => setCancelId(null));
  };

  const nextStatus = (current) => STATUS_WORKFLOW[current];

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle} />
      {error && <p className="text-red-600 mb-4">{error}</p>}

      <OrdersToolbar
        search={search}
        onSearchChange={setSearch}
        dateFilter={dateFilter}
        onDateFilterChange={setDateFilter}
        customFrom={customFrom}
        customTo={customTo}
        onCustomFromChange={setCustomFrom}
        onCustomToChange={setCustomTo}
        sortBy={sortBy}
        onSortChange={setSortBy}
      />

      <OrderMonthGroups
        groups={groups}
        showActions={showAdvance || showCancel}
        onAdvance={showAdvance ? handleAdvance : undefined}
        nextStatus={showAdvance ? nextStatus : undefined}
        onCancel={showCancel ? setCancelId : undefined}
      />

      <ReportExportBar
        onExport={(params) =>
          runOrdersReportExport({
            ...params,
            orders,
            category: title,
            includeRevenueStats: true,
          })
        }
      />

      <ConfirmDialog
        open={!!cancelId}
        title="Cancel Order"
        message="Cancel this order and restore product stock? This cannot be undone."
        confirmLabel="Cancel order"
        immediateClose
        onConfirm={handleCancel}
        onCancel={() => setCancelId(null)}
      />
    </div>
  );
}
